import styled from '@emotion/styled';

/**
 * Поле целиком: зона, подсказка и выбранные картинки.
 *
 * Белая подложка со скруглением — то же, что было у обеих прежних дропзон:
 * каждая заворачивала себя в такой контейнер, а экраны добавляли к нему только
 * отступы. Поэтому подложка живёт здесь, а не повторяется в семи местах.
 */
export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  background-color: ${({ theme }) => theme.colors.SurfaceCard};
  border-radius: ${({ theme }) => theme.radii.card};
`;

/**
 * Сама зона.
 *
 * Состояния — классами: так их видно в тестах и так же было в обеих прежних
 * дропзонах. Отличие в том, что теперь класс действительно ставится: в новой
 * дропзоне `.active` и `.focused` были описаны в стилях, но `isDragActive` наружу
 * не отдавался, и зона не отзывалась на перетаскивание вовсе.
 */
export const Zone = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;

  width: 100%;
  min-height: 120px;
  padding: 20px;

  border-radius: ${({ theme }) => theme.radii.card};
  border: 1px dashed ${({ theme }) => theme.colors.SurfaceRomanSilver30};
  background-color: ${({ theme }) => theme.colors.SurfaceCultured10};
  transition:
    border-color 0.25s ease,
    background-color 0.25s ease;

  &.focused {
    border-color: ${({ theme }) => theme.colors.SurfaceRichBlack60};
  }

  /* Заливка меняется вместе с рамкой: одной рамки в 1px не хватает, чтобы
     заметить, что файл можно отпускать именно здесь. */
  &.active {
    border-color: ${({ theme }) => theme.colors.SurfaceRichBlack60};
    background-color: ${({ theme }) => theme.colors.SurfacePlatinum20};
  }

  &.error {
    border-color: ${({ theme }) => theme.colors.error};
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Контейнер остаётся наведённым ради курсора, а всё внутри — нет. */
  &.disabled * {
    pointer-events: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.SurfaceRichBlack60};
    outline-offset: 2px;
  }
`;

/**
 * Тело зоны.
 *
 * Позиционируется, чтобы рассказ о сжатии лёг поверх приглашения выбрать файл,
 * а не вместо него: подменять одно другим значит менять высоту зоны, и вёрстка
 * дёргалась бы на каждый выбор файла и обратно.
 */
export const Body = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/** Приглашение выбрать файл. На время сжатия прячется, но место занимает. */
export const Invitation = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &.hidden {
    visibility: hidden;
  }
`;

/** Пока идёт сжатие — поверх спрятанного приглашения, по центру зоны. */
export const Processing = styled.div`
  position: absolute;
  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: ${({ theme }) => theme.colors.SurfaceRomanSilver30};

  /* Спиннер — такой же flex-элемент, как и подпись рядом. Без запрета на сжатие
     текст «Сжимаем изображение…» отбирает у него ширину, и круг становится овалом. */
  > span {
    flex: none;
  }
`;

export const PreviewList = styled.div`
  display: flex;
  /* Перенос строк обязателен: пять фотографий локации в одну строку не влезают. */
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

/** По этому классу карточка находит свой крестик удаления. */
export const REMOVE_BUTTON_CLASS = 'dropzone-remove';

/**
 * Карточка одной картинки.
 *
 * Крестик показывается по наведению на карточку, и правило про это живёт здесь,
 * а не в самом крестике. Написать в нём `${PreviewCard}:hover &` нельзя: селектор
 * по стилизованному компоненту работает только с `@emotion/babel-plugin`, а в
 * сборке проекта его нет — вместо неприменённого стиля страница падает с
 * «Component selectors can only be used in conjunction with @emotion/babel-plugin».
 */
export const PreviewCard = styled.div<{ size: number }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  /* Ширина карточки — ровно ширина миниатюры. Иначе длинное имя файла растягивает
     карточку, и крестик, привязанный к её правому верхнему углу, уезжает вбок от
     картинки, а подписи соседних превью наезжают друг на друга. */
  width: ${({ size }) => size}px;

  &:hover .${REMOVE_BUTTON_CLASS} {
    opacity: 1;
  }
`;

export const Thumb = styled.div<{ size: number }>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  min-width: ${({ size }) => size}px;

  overflow: hidden;
  border-radius: ${({ theme }) => theme.radii.card};
  border: 1px solid ${({ theme }) => theme.colors.SurfaceRomanSilver30};
  background-color: ${({ theme }) => theme.colors.SurfaceCultured10};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const RemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 1;

  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 4px;

  border-radius: ${({ theme }) => theme.radii.pill};
  background: ${({ theme }) => theme.colors.AlphaWhite};
  color: ${({ theme }) => theme.colors.SurfaceRichBlack60};
  cursor: pointer;

  opacity: 0;
  transition:
    opacity 0.2s ease-in-out,
    background-color 0.2s ease-in-out,
    color 0.2s ease-in-out;

  /* Наведение на сам крестик отзывается отдельно от наведения на карточку:
     иначе непонятно, удалит ли клик картинку или просто откроет её. */
  &:hover {
    background: ${({ theme }) => theme.colors.SurfaceCard};
    color: ${({ theme }) => theme.colors.DarkYellow};
  }

  &:focus-visible {
    opacity: 1;
  }
`;

/**
 * Имя файла в одну строку: длинное имя не должно растягивать колонку с миниатюрами.
 *
 * `display: block` обязателен — у строчного элемента `text-overflow` не работает,
 * и имя вылезало за карточку поверх соседних превью.
 */
export const FileName = styled.span`
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
`;
